// This is the React component for Section 7 ("IMPLEMENTATION")
// It uses GSAP's matchMedia to create two different layouts & animations:
// 1. Desktop: A pinned section with 'position: absolute' items staggering in.
// 2. Mobile/Tablet: A stacked layout with a simple waterfall reveal.

import React, { useLayoutEffect, useRef } from "react";
import "./Section7.css"; // We will create this file next
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// --- Component Text ---
// An array of objects for the 4 implementation steps.
const implementationSteps = [
  {
    number: "1",
    title: "OPENING REFLECTION AND WARM-UP",
    text: "Come together and check-in. Opportunity to set an intention or reconnect.",
  },
  {
    number: "2",
    title: "MAIN ACTIVITY",
    text: "A shared moment that incorporates games, adventures, land-based learning or storytelling",
  },
  {
    number: "3",
    title: "CONNECTION AND COMMUNITY",
    text: "Not to be confused as a lecture, but rather an exchange where topics like stress management, nutrition, gratitude, sleep, safety and confidence are openly shared.",
  },
  {
    number: "4",
    title: "COOL-DOWN AND REFLECTION",
    text: "The space to come together one last time with an opportunity to share and reflect",
  },
];

// --- React Component ---
function Section7() {
  const sectionRef = useRef(null);
  // We need two sets of refs: one for desktop items, one for mobile
  const desktopItemRefs = useRef([]);
  const mobileItemRefs = useRef([]);

  useLayoutEffect(() => {
// --- THIS IS THE FIX ---
    // This correctly trims the ref arrays to the right length
    // without deleting the items inside them.
    desktopItemRefs.current = desktopItemRefs.current.slice(0, implementationSteps.length);
    mobileItemRefs.current = mobileItemRefs.current.slice(0, implementationSteps.length);

    const ctx = gsap.context(() => {
      ScrollTrigger.matchMedia({
        // --- 1. DESKTOP ANIMATION (Pinned, Absolute) ---
        "(min-width: 1024px)": function () {
          const items = desktopItemRefs.current;

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              pin: true,
              start: "top top",
              end: "+=2000", // Pin for 2000px
              scrub: 1, // Link to scrollbar
              toggleActions: "play reverse play reverse",
              // Add a smooth leave action
              onLeave: (self) => {
                gsap.to(self.animation, {
                  progress: 1,
                  duration: 0.5,
                  ease: "power1.out",
                });
                self.disable();
                self.pin && self.pin.style.removeProperty("transform");
                ScrollTrigger.refresh();
              },
            },
          });

          // Stagger-fade in the 4 items
          tl.fromTo(
            items,
            { opacity: 0 },
            { opacity: 1, duration: 1, stagger: 0.25 }
          );
        },

        // --- 2. TABLET & MOBILE ANIMATION (Waterfall) ---
        "(max-width: 1023px)": function () {
          const items = mobileItemRefs.current;

          gsap.fromTo(
            items,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.2, // Simple waterfall
              ease: "power2.out",
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 80%",
                toggleActions: "play none none none",
                once: true,
              },
            }
          );
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Helper function to add refs to the arrays
  const addDesktopRef = (el) => {
    if (el && !desktopItemRefs.current.includes(el)) {
      desktopItemRefs.current.push(el);
    }
  };

  const addMobileRef = (el) => {
    if (el && !mobileItemRefs.current.includes(el)) {
      mobileItemRefs.current.push(el);
    }
  };

  // --- JSX (HTML) ---
  return (
    <section className="section-7" ref={sectionRef}>
      
      {/* --- Desktop Layout (Absolute Positioned) --- */}
      {/* This is hidden on mobile by CSS */}
      <div className="section-7-desktop-container">
        {implementationSteps.map((item, index) => (
          <div
            // We add a class 'item-1', 'item-2', etc. for CSS positioning
            className={`section-7-item-desktop item-${index + 1}`}
            key={index}
            ref={addDesktopRef} // Add to desktop refs array
          >
            <span className="section-7-number">{item.number}</span>
            <div className="section-7-text">
              <h3 className="section-7-title">{item.title}</h3>
              <p className="section-7-description">{item.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* --- Mobile/Tablet Layout (Stacked) --- */}
      {/* This is hidden on desktop by CSS */}
      <div className="section-7-mobile-container">
        {implementationSteps.map((item, index) => (
          <div
            className="section-7-item-mobile"
            key={index}
            ref={addMobileRef} // Add to mobile refs array
          >
            <span className="section-7-number">{item.number}</span>
            <div className="section-7-text">
              <h3 className="section-7-title">{item.title}</h3>
              <p className="section-7-description">{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Section7;